import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import HoverTip from "@/components/HoverTip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "clientName", helpId: "client", id: "client", label: "Client name *", testId: "save-client-name", placeholder: "Mark Armstrong" },
  { name: "businessName", helpId: "business", id: "business", label: "Business", testId: "save-business-name", placeholder: "Bay Roofing Ltd." },
  { name: "notes", helpId: "notes", id: "notes", label: "Notes", testId: "save-notes", placeholder: "Discussed 15-truck fleet, 4 foremen, Q2 review" },
];

const EMPTY_FORM = { clientName: "", businessName: "", notes: "" };

export default function SaveScenarioDialog({ inputs, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const setField = (name) => (value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    if (!form.clientName.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/scenarios`, {
        client_name: form.clientName.trim(),
        business_name: form.businessName.trim() || null,
        notes: form.notes.trim() || null,
        inputs,
      });
      toast.success(`Scenario saved for ${form.clientName}`);
      onSaved?.(form.clientName.trim());
      setOpen(false);
      setForm(EMPTY_FORM);
    } catch (e) {
      toast.error("Failed to save scenario", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="btn-save-scenario"
          className="bg-brand-forest hover:bg-brand-forestHover text-brand-bone rounded-md gap-2"
        >
          <FloppyDisk size={16} weight="bold" /> Save scenario
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-brand-line max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-forest">
            Save this scenario
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-muted">
            Stores this breakdown in your private workspace for follow-ups and comparisons.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {FIELDS.map(({ name, ...field }) => (
            <DialogField key={name} {...field} value={form[name]} onChange={setField(name)} />
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="border-brand-line rounded-md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="btn-confirm-save"
            className="bg-brand-forest hover:bg-brand-forestHover text-brand-bone rounded-md"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogField({ helpId, label, id, testId, value, onChange, placeholder }) {
  return (
    <div>
      <HoverTip id={helpId} label={label} htmlFor={id} />
      <Input
        id={id}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 bg-white border-brand-line focus-visible:ring-brand-forest"
      />
    </div>
  );
}
